/**
 * Dashboard Page
 * Main dashboard for admin portal - shows content based on user permissions
 */

import { useQuery } from '@tanstack/react-query'
import { Activity, Clock, CheckCircle, XCircle, TrendingUp, Users, DollarSign } from 'lucide-react'
import { Link } from 'react-router-dom'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { LoadingState, ErrorState } from '@/components/ui/loading-state'
import { MetricCard } from '@/features/execution-monitoring/components/MetricCard'
import { KafkaMetricsCard } from '@/features/execution-monitoring/components/KafkaMetricsCard'
import { getMonitoringStats } from '@/features/execution-monitoring/queries/executions'
import { useAuth, PERMISSIONS } from '@/contexts/AuthContext'
import { useAccount } from '@/contexts/AccountContext'
import type { MonitoringStatsResponse } from '@/features/execution-monitoring/types'

export function DashboardPage() {
  const { hasPermission } = useAuth()
  const { selectedAccountId } = useAccount()

  // Check user permissions
  const canViewCampaignMonitoring = hasPermission(PERMISSIONS.CAMPAIGN_MONITORING_DASHBOARD)
  const canViewFinance = hasPermission(PERMISSIONS.FINANCE_MONITORING_DASHBOARD)

  // Parse account ID safely
  const parsedAccountId = selectedAccountId && selectedAccountId !== 'ALL'
    ? parseInt(selectedAccountId)
    : undefined

  const {
    data: stats,
    isLoading,
    error,
    refetch,
  } = useQuery<MonitoringStatsResponse>({
    queryKey: ['dashboard-stats', selectedAccountId],
    queryFn: () => getMonitoringStats(1, parsedAccountId),
    refetchInterval: 30000, // Refresh every 30 seconds
    // Only fetch if user can view campaign monitoring
    enabled: canViewCampaignMonitoring,
  })

  // Show loading only if campaign monitoring is enabled and loading
  if (canViewCampaignMonitoring && isLoading) {
    return (
      <div className="p-6">
        <LoadingState message="Loading dashboard..." />
      </div>
    )
  }

  if (canViewCampaignMonitoring && error) {
    return (
      <div className="p-6">
        <ErrorState
          title="Failed to load dashboard"
          message={(error as Error).message}
          onRetry={() => refetch()}
        />
      </div>
    )
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-gray-500 mt-1">
          Welcome to FirstHive Admin Portal
        </p>
      </div>

      {/* Campaign Monitoring Quick Stats - Only for users with CAMPAIGN_MONITORING_DASHBOARD permission */}
      {canViewCampaignMonitoring && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <MetricCard
              title="Active Executions"
              value={stats?.activeExecutions || 0}
              icon={<Activity className="h-5 w-5" />}
              color="blue"
            />
            <MetricCard
              title="Running"
              value={stats?.byStatus?.RUNNING || 0}
              icon={<Clock className="h-5 w-5" />}
              color="yellow"
            />
            <MetricCard
              title="Completed Today"
              value={stats?.byStatus?.COMPLETED || 0}
              icon={<CheckCircle className="h-5 w-5" />}
              color="green"
            />
            <MetricCard
              title="Failed"
              value={stats?.byStatus?.FAILED || 0}
              icon={<XCircle className="h-5 w-5" />}
              color="red"
            />
          </div>

          {/* Kafka Event Metrics */}
          <KafkaMetricsCard
            topics={['custom-raw', 'datalist-raw']}
            days={7}
            title="Event Ingestion (Kafka)"
          />
        </>
      )}

      {/* Quick Access Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* Execution Monitoring - Only for users with CAMPAIGN_MONITORING_DASHBOARD permission */}
        {canViewCampaignMonitoring && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Activity className="h-5 w-5 text-blue-500" />
                Execution Monitoring
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-600 text-sm mb-4">
                Monitor campaign executions in real-time. View progress, metrics, and troubleshoot issues.
              </p>
              <div className="flex gap-2 mb-4">
                <div className="text-center px-4 py-2 bg-blue-50 rounded-lg">
                  <div className="text-2xl font-bold text-blue-700">
                    {stats?.byCampaignType?.ONE_TIME || 0}
                  </div>
                  <div className="text-xs text-blue-600">One-Time</div>
                </div>
                <div className="text-center px-4 py-2 bg-purple-50 rounded-lg">
                  <div className="text-2xl font-bold text-purple-700">
                    {stats?.byCampaignType?.RECURRING || 0}
                  </div>
                  <div className="text-xs text-purple-600">Recurring</div>
                </div>
                <div className="text-center px-4 py-2 bg-orange-50 rounded-lg">
                  <div className="text-2xl font-bold text-orange-700">
                    {stats?.byCampaignType?.AB_TEST || 0}
                  </div>
                  <div className="text-xs text-orange-600">A/B Tests</div>
                </div>
              </div>
              <Link to="/executions">
                <Button className="w-full">View Executions</Button>
              </Link>
            </CardContent>
          </Card>
        )}

        {/* Finance Dashboard - Only for users with FINANCE_MONITORING_DASHBOARD permission */}
        {canViewFinance && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="h-5 w-5 text-green-500" />
                Finance Dashboard
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-600 text-sm mb-4">
                View account usage, license utilization, data flow reports, and financial metrics.
              </p>
              <Link to="/finance">
                <Button className="w-full">View Finance</Button>
              </Link>
            </CardContent>
          </Card>
        )}

        {/* MongoDB Viewer - Coming Soon - matches the same permission that gates the real page */}
        {hasPermission(PERMISSIONS.MONGO_CONSOLE_VIEW) && (
          <Card className="opacity-60">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-green-500" />
                MongoDB Viewer
                <span className="text-xs bg-gray-200 px-2 py-0.5 rounded-full ml-auto">
                  Coming Soon
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-600 text-sm mb-4">
                Browse and query MongoDB collections directly. View campaign data, executions, and more.
              </p>
              <Button disabled className="w-full" variant="outline">
                Coming Soon
              </Button>
            </CardContent>
          </Card>
        )}

        {/* API Keys - Coming Soon - matches the same permission that gates the real page */}
        {hasPermission(PERMISSIONS.API_KEY_MANAGEMENT) && (
          <Card className="opacity-60">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5 text-purple-500" />
                API Key Management
                <span className="text-xs bg-gray-200 px-2 py-0.5 rounded-full ml-auto">
                  Coming Soon
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-600 text-sm mb-4">
                Manage API keys for external integrations. Create, revoke, and monitor API key usage.
              </p>
              <Button disabled className="w-full" variant="outline">
                Coming Soon
              </Button>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Phase Distribution */}
      {stats?.activeByPhase && Object.keys(stats.activeByPhase).length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Active Executions by Phase</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-4">
              {Object.entries(stats.activeByPhase).map(([phase, count]) => (
                <div
                  key={phase}
                  className="flex items-center gap-2 bg-gray-50 px-4 py-2 rounded-lg"
                >
                  <span className="text-gray-700 font-medium">
                    {phase.replace(/_/g, ' ')}
                  </span>
                  <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full text-sm font-semibold">
                    {String(count)}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Channel Distribution */}
      {stats?.byChannel && Object.keys(stats.byChannel).length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Executions by Channel</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-4">
              {Object.entries(stats.byChannel).map(([channel, count]) => (
                <div
                  key={channel}
                  className="flex items-center gap-2 bg-gray-50 px-4 py-2 rounded-lg"
                >
                  <span className="text-gray-700 font-medium">{channel}</span>
                  <span className="bg-green-100 text-green-700 px-2 py-0.5 rounded-full text-sm font-semibold">
                    {String(count)}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}

export default DashboardPage
